import React, { useState, useEffect } from "react";
import useCustomerLogin from "@src/hooks/useCustomerLogin";
import AccountForgotPassword from "@project-stories/ecommerce/Account/AccountForgotPassword";
import useCustomerAccessToken from "@src/hooks/useCustomerAccessToken";
import useCustomerActivationCheck from "@src/hooks/useCustomerActivationCheck";

const AccountLoginForm = ({ settings, isAccountForgotPasswordInvalid }) => {
    const { loginCustomer, error, success } = useCustomerLogin();
    const { getAccessToken, errorMessage: accessError, successMessage: accessSuccess } = useCustomerAccessToken();
    const { checkCustomerActivationStatus } = useCustomerActivationCheck();
    const [email, setEmail] = useState("");
    const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [showInvalidToken, setShowInvalidToken] = useState(false);
    const [activationMessage, setActivationMessage] = useState("");
    const [isLoggingIn, setIsLoggingIn] = useState(false);

    useEffect(() => {
        if (isAccountForgotPasswordInvalid){
            setShowForgotPassword(true);
            setShowInvalidToken(true);
        }
    }, [isAccountForgotPasswordInvalid]);

    const showCreateAccountButton = false;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoggingIn(true);
        setActivationMessage("");

        const data: any = await getAccessToken(email, password);

        if (data?.success) {
            await loginCustomer(email, password);

        } else if (data?.includes('Unidentified customer')) {
            const activationResult = await checkCustomerActivationStatus(email);

            if (activationResult
                && activationResult.exists
                && !activationResult.isActivated
                && activationResult.activationEmailSent
            ) {
                setActivationMessage(activationResult.message);
            }
            setIsLoggingIn(false);

        } else {
            setIsLoggingIn(false);
        }
    }

    const toggleForgotPassword = (e) => {
        console.log('toggleForgotPassword');
        e.preventDefault();
        setShowForgotPassword(!showForgotPassword);
        setShowInvalidToken(false);
    }

    const handleCancel = () => {
        if (settings?.extension?.is_popup) {
            const closeEvent = new CustomEvent("toggle-display-open", {
                detail: { message: "header-signin" },
            });
            window.dispatchEvent(closeEvent);

        } else {
            window.location.href = "/";
        }
    }

    useEffect(() => {
        function handleToggleDisplayOpen(event) {
            if (event?.detail?.message?.type === 'reset-password') {
                toggleForgotPassword(event);
                setForgotPasswordEmail(event?.detail?.message?.data?.email)
            }
        }

        window.addEventListener("toggle-display-open", handleToggleDisplayOpen);

        return () => {
            window.removeEventListener("toggle-display-open", handleToggleDisplayOpen);
        };
    }, []);

    return (
        <>
            {!showForgotPassword && <form data-shopify-captcha="true" id="loginForm"
                onSubmit={handleSubmit}
                className="account-page__form">

                <div className={`${(error || accessError || success || accessSuccess || activationMessage) ? 'py-[16px]' : ''}`}>
                    {!isLoggingIn && activationMessage && (
                        <p className="text-center b3" style={{ color: '#36573B', backgroundColor: '#f0f8f0', padding: '12px', borderRadius: '4px', border: '1px solid #36573B' }}>
                            {activationMessage}
                        </p>
                    )}

                    {!isLoggingIn && !activationMessage && (error || accessError) && (
                        <p className="text-center b3" style={{ color: 'red' }}>
                            {accessError && accessError.includes("Unidentified")
                                ? settings.section.incorrect_details_message
                                : accessError || error}
                        </p>
                    )}
                    
                    {(success || accessSuccess) && <p style={{ color: 'green' }}>{settings.section.success_message}</p>}
                </div>

                <div className={`account-page__input-container  space-y-[16px]`}>

                    <div className={`account-page__input-container floating-input`}>

                        <input
                            type="email"
                            id="login_email"
                            name={"email"}
                            placeholder={" "}
                            className="account-page__input form-field"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                        <label htmlFor="email">Email</label>
                    </div>

                    <div className={`account-page__input-container floating-input`}>

                        <input
                            type="password"
                            id="password"
                            placeholder={" "}
                            name={"password"}
                            className="account-page__input form-field w-full p-2 tracking-[0.35rem]"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                        />
                        <label htmlFor="password">Password</label>
                    </div>
                </div>

                <p className="flex justify-end mb-4 font-medium account-page__forgot-password forgot-password blabel">
                    <a href="#forgotpassword" onClick={toggleForgotPassword}>
                        Forgot password?
                    </a>
                </p>

                <div className={`account-page__actions-container space-y-[16px]`}>
                    <div className={'flex flex-col'}>
                        <button
                            type="submit"
                            className="w-full account-page__submit-btn button btn-dark"
                            disabled={isLoggingIn}
                        >
                            {(isLoggingIn) ? "Signing in..." : "Sign In"}
                        </button>
                    </div>

                    {showCreateAccountButton && <div className={'flex flex-col submit'}>
                        <a href="/account/register" className="w-full account-page__submit-btn button btn-outline">
                            {"Create Account"}
                        </a>
                    </div>}

                    <div className="text-sm font-light text-center">
                        <div className="hidden my-2 font-light account-inquiry">
                            <span>Don't have an account? <a href="{{ routes.account_register_url }}">Sign Up.</a></span>
                        </div>

                        <div className="font-medium text-center account-inquiry blabel">
                            <span onClick={() => handleCancel()}>Cancel</span>
                        </div>

                    </div>
                </div>
            </form>}

            {showForgotPassword && <AccountForgotPassword
                settings={settings}
                initialEmail={forgotPasswordEmail}
                isAccountForgotPasswordInvalid={isAccountForgotPasswordInvalid}
                showInvalidToken={showInvalidToken}
                onCancel={toggleForgotPassword}/>
            }
        </>
    );
};

export default AccountLoginForm;
